const { Core, Utils, Events } = Global;
const QUANTITY_DEBOUNCE = 180;


customElements.define('cart-provider', class extends Core {
    liveSections = new Set();
    notificationsEnabled = false;
    quantityChanges = new Map();
    queue = Promise.resolve();
    queuedOperations = 0;
    keyAliases = new Map();

    render() {
        this.sub(Events.CART_REGISTER, this._registerSection, { global: true });
        this.sub(Events.CART_NOTIFICATIONS_ENABLING, this._enableNotifications, { global: true });
        this.sub(Events.CART_ADD, this._addToCart, { global: true });
        this.sub(Events.CART_CHANGE, this._updateCart, { global: true });
        this.sub(Events.CART_REPLACE, this._replaceCartItem, { global: true });
        this.sub(Events.PRODUCT_FORM_SUBMIT, this._formSubmitHandler, { global: true });

        this._setExternalAPI();
        try { this.cart = JSON.parse(this.querySelector('[data-cart-state]')?.textContent || 'null'); } catch { this.cart = null; }
        this.checkoutGuard = event => {
            if (!this.busy && !this.unsynced) return;
            const checkout = event.target.closest?.('[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons');
            const cartForm = event.type === 'submit' && event.target.id?.startsWith('cartForm-');
            if (!checkout && !cartForm) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this._status(this.unsynced ? 'Unable to verify your cart. Please reload before checking out.' : 'Saving your cart. Checkout will be ready shortly.');
        };
        document.addEventListener('click', this.checkoutGuard, true);
        document.addEventListener('submit', this.checkoutGuard, true);
    }

    _registerSection(section) {
        this.liveSections.add(section);
    }
    
    _enableNotifications() {
        this.notificationsEnabled = true;
    }

    _formSubmitHandler({ formData }) {
        this._flushQuantities();
        return this._enqueue(async () => {
            this.notificationsEnabled && formData.append("attributes[notification_items_variant_ids]", formData.get('id'));
            formData.set('sections', Array.from(this.liveSections).join(','));
            formData.set('sections_url', window.location.pathname);
            const config = {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/javascript'
                },
                body: formData
            };
            const res = await fetch(`${window.dynamicURLs['cartAdd']}.js`, config);
            const cartData = await this._readResponse(res);
            this.cart = null; // Resolve fresh keys lazily before the next line mutation.
            this._stageUpdate(cartData, 'cartAdd');
            if (this.notificationsEnabled) await this._cleanCartAttributes();
        });
    }
    
    _addToCart({ items }) {
        this._flushQuantities();
        return this._enqueue(async () => {
            await this._requestCart('cartAdd', {
                items,
                ...(this.notificationsEnabled && { attributes: {
                    notification_items_variant_ids: items.map(({id}) => id).join(',')
                }})
            });
            this.cart = null;
            if (this.notificationsEnabled) await this._cleanCartAttributes();
        });
    }

    _cleanCartAttributes() {
        return this._fetchCart('cartUpdate', { attributes: { notification_items_variant_ids: null }})
            .then(response => this._readResponse(response))
            .then(cart => this._acceptCart(cart));
    }

    _fetchCart(route, body) {
        return fetch(`${window.dynamicURLs[route]}.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(
                body
            )
        })
    }

    _updateCart({ quantity, key, line }) {
        quantity = Number(quantity);
        if (!Number.isInteger(quantity) || quantity < 0) return;
        key ||= this.cart?.items[line - 1]?.key;
        if (!key) return;
        if (this.quantityChanges.get(key)?.quantity === quantity) return;
        if (!this.busy && !this.unsynced && this.cart?.items.find(item => item.key === this._resolveKey(key))?.quantity === quantity) return;
        this.quantityChanges.set(key, { quantity });
        // Schedule first: a UI/app exception must not leave an intention with
        // no timer, permanently blocking checkout without a server request.
        clearTimeout(this.quantityTimer);
        this.quantityTimer = setTimeout(() => this._flushQuantities(), QUANTITY_DEBOUNCE);
        try {
            this._optimisticQuantity(key, quantity);
        } catch (error) {
            console.error('Unable to render pending cart quantity', error);
        }
        this._setBusy();
    }

    _flushQuantities() {
        clearTimeout(this.quantityTimer);
        if (this.quantityFlushQueued || !this.quantityChanges.size) return;
        this.quantityFlushQueued = true;
        const flush = this._enqueue(async () => {
            await this._ensureCart();
            while (this.quantityChanges.size) {
                const [key, change] = this.quantityChanges.entries().next().value;
                const resolved = this._resolveKey(key);
                const item = this.cart.items.find(item => item.key === resolved);
                if (!item) throw new Error('This cart item changed. Please check the updated cart.');
                const data = await this._requestCart('cartChange', { id: resolved, quantity: change.quantity });
                this._acceptCart(data);
                if (this.quantityChanges.get(key) === change) this.quantityChanges.delete(key);
            }
        });
        // Also release the latch when the queue rejects before calling the
        // operation (for example, an earlier request left the cart unsynced).
        return flush.finally(() => {
            this.quantityFlushQueued = false;
            if (this.quantityChanges.size) this._flushQuantities();
        });
    }

    _replaceCartItem({ key, line, newItem, quantity, useCurrentQuantity, maxQuantity }) {
        // Drain quantity intentions first so replacement uses the current line.
        this._flushQuantities();
        return this._enqueue(async () => {
            await this._ensureCart();
            const original = this.cart.items.find(item => item.key === this._resolveKey(key)) || (!key && this.cart.items[line - 1]);
            if (!original) throw new Error('This cart item is no longer available.');
            if (String(original.variant_id) === String(newItem)) return;
            if (useCurrentQuantity) {
                quantity = original.quantity;
                if (maxQuantity !== undefined) quantity = Math.min(quantity, Number(maxQuantity));
            }
            if (!Number.isInteger(Number(quantity)) || Number(quantity) < 1) throw new Error('The selected variant is unavailable.');
            const replacement = { id: Number(newItem), quantity: Number(quantity), properties: original.properties || {} };
            if (original.selling_plan_allocation) replacement.selling_plan = original.selling_plan_allocation.selling_plan.id;
            // Add first: a rejected replacement must never delete the original.
            await this._requestCart('cartAdd', { items: [replacement] });
            await this._readCart();
            const originalKey = this._resolveKey(original.key);
            const data = await this._requestCart('cartChange', { id: originalKey, quantity: 0 });
            this._acceptCart(data);
        });
    }

    async _requestCart(route, body) {
        const response = await this._fetchCart(route, {
            ...body,
            sections: Array.from(this.liveSections).join(','),
            sections_url: window.location.pathname
        });
        const data = await this._readResponse(response);
        this._stageUpdate(data, route);
        return data;
    }

    async _readResponse(response) {
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || window.cartStrings?.error || 'Unable to update cart.');
        return data;
    }

    _stageUpdate(data, req) {
        this.latestUpdate = { sections: data.sections, req: req === 'cartAdd' || this.latestUpdate?.req === 'cartAdd' ? 'cartAdd' : req };
    }

    _enqueue(operation) {
        this.queuedOperations++;
        if (!this.unsynced) this.errorMessage = '';
        this._setBusy();
        this.queue = this.queue.then(async () => {
            try {
                if (this.unsynced) throw new Error('Please reload to verify your cart before making more changes.');
                await operation();
            } catch (error) {
                this.quantityChanges.clear();
                clearTimeout(this.quantityTimer);
                this.errorMessage = error.message;
                this.pub(Events.CART_ERROR, error);
                this.pub(Events.TOAST_NOTIFICATION, { type: 'error', msg: error.message });
                try { await this._refreshState(); } catch { this.unsynced = true; }
            } finally {
                this.queuedOperations--;
                if (!this.queuedOperations && !this.quantityChanges.size) {
                    this.settling = true;
                    try { await this._publishSettled(); } finally { this.settling = false; }
                }
                this._setBusy();
            }
        }).catch(error => {
            // A third-party render subscriber must not permanently reject the
            // queue or accidentally enable checkout with stale markup.
            this.unsynced = true;
            this._setBusy();
            console.error(error);
        });
        return this.queue;
    }

    async _publishSettled() {
        if (this.latestUpdate && !this.unsynced) {
            const update = this.latestUpdate;
            this.latestUpdate = null;
            // Section rendering can fail even when the mutation succeeded.
            if (!update.sections || [...this.liveSections].some(id => !update.sections[id])) {
                try { update.sections = await this._fetchSections(); } catch { this.unsynced = true; }
            }
            if (this.quantityChanges.size || this.queuedOperations) {
                this.latestUpdate = update;
                return;
            }
            if (!this.unsynced) {
                const notes = [...this.querySelectorAll('[name="note"]')].map(input => [input.id, input.value]);
                const active = document.activeElement;
                const focus = active?.matches('[name="updates[]"]') ? { id: active.id } : null;
                this.pub(Events.CART_UPDATE, update);
                notes.forEach(([id, value]) => {
                    const input = document.getElementById(id);
                    if (input) input.value = value;
                });
                if (focus) document.getElementById(focus.id)?.focus({ preventScroll: true });
            }
        }
        this.querySelectorAll('[data-cart-pending], [data-cart-removing]').forEach(row => {
            row.removeAttribute('data-cart-pending');
            row.removeAttribute('data-cart-removing');
        });
    }

    _identity(item) {
        return JSON.stringify([item.variant_id, Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan.id || null]);
    }

    _acceptCart(cart) {
        // Discount changes can toggle a line key back to a previously seen key.
        // A currently authoritative key must never point at an older alias.
        cart.items.forEach(item => this.keyAliases.delete(item.key));
        for (const previous of this.cart?.items || []) {
            if (cart.items.some(item => item.key === previous.key)) continue;
            const matches = cart.items.filter(item => this._identity(item) === this._identity(previous));
            if (matches.length === 1) this.keyAliases.set(previous.key, matches[0].key);
        }
        this.cart = cart;
    }

    _resolveKey(key) {
        const seen = new Set();
        while (this.keyAliases.has(key) && !seen.has(key)) {
            seen.add(key);
            key = this.keyAliases.get(key);
        }
        return key;
    }

    async _readCart() {
        const response = await fetch(`${window.dynamicURLs.cart}.js`, { cache: 'no-store' });
        this._acceptCart(await this._readResponse(response));
    }

    async _ensureCart() {
        if (!this.cart) await this._readCart();
    }

    _optimisticQuantity(key, quantity) {
        this.querySelectorAll('[data-item-key]').forEach(row => {
            if (this._resolveKey(row.dataset.itemKey) !== this._resolveKey(key)) return;
            row.setAttribute('data-cart-pending', '');
            row.toggleAttribute('data-cart-removing', quantity === 0);
            row.querySelectorAll('[name="updates[]"]').forEach(input => {
                input.value = quantity;
                input.dataset.lastValid = String(quantity);
            });
            row.querySelectorAll('[data-cart-quantity]').forEach(label => { label.textContent = quantity; });
            row.querySelectorAll('cart-qty[step]').forEach(control => {
                const max = Number(row.dataset.maxQuantity);
                const step = Number(control.getAttribute('step'));
                const disabled = step > 0 && row.hasAttribute('data-max-quantity') && quantity >= max;
                control.toggleAttribute('disabled', disabled);
                control.classList.toggle('@disabled', disabled);
            });
        });
    }

    get busy() { return !!(this.settling || this.queuedOperations || this.quantityChanges.size); }

    _status(message) {
        const status = this.querySelector('[data-cart-sync-status]');
        if (!status) return;
        status.textContent = message;
        status.hidden = !message;
    }

    _setBusy() {
        const blocked = this.busy || this.unsynced;
        this.toggleAttribute('data-cart-syncing', this.busy);
        this.toggleAttribute('data-cart-unsynced', !!this.unsynced);
        this.querySelectorAll('[name="checkout"]').forEach(button => {
            if (blocked) {
                if (!button.hasAttribute('data-cart-sync-disabled')) button.dataset.cartSyncDisabled = String(button.disabled);
                button.disabled = true;
            } else if (button.hasAttribute('data-cart-sync-disabled')) {
                button.disabled = button.dataset.cartSyncDisabled === 'true';
                delete button.dataset.cartSyncDisabled;
            }
            button.setAttribute('aria-disabled', String(button.disabled));
        });
        this.querySelectorAll('.additional-checkout-buttons').forEach(wrapper => {
            if (blocked) {
                if (!wrapper.hasAttribute('data-cart-sync-inert')) wrapper.dataset.cartSyncInert = String(wrapper.inert);
                wrapper.inert = true;
            } else if (wrapper.hasAttribute('data-cart-sync-inert')) {
                wrapper.inert = wrapper.dataset.cartSyncInert === 'true';
                delete wrapper.dataset.cartSyncInert;
            }
        });
        this._status(this.unsynced ? 'Unable to verify your cart. Please reload before checking out.' :
            this.busy ? 'Saving your cart… Totals will update when saved.' : this.errorMessage || '');
    }

    _setExternalAPI() {
        this.onCartRefresh = () => this._cartRefresh();
        document.addEventListener('cart:refresh', this.onCartRefresh);
    }

    _cartRefresh() {
        this._flushQuantities();
        return this._enqueue(() => this._refreshState());
    }

    async _fetchSections() {
        const url = new URL(window.dynamicURLs.cart, window.location.origin);
        url.searchParams.set('sections', [...this.liveSections].join(','));
        const response = await fetch(url, { cache: 'no-store' });
        const sections = await this._readResponse(response);
        if ([...this.liveSections].some(id => !sections[id])) throw new Error('Unable to render updated cart.');
        return sections;
    }

    async _refreshState() {
        await this._readCart();
        this.latestUpdate = { sections: await this._fetchSections(), req: 'refresh' };
        this.unsynced = false;
    }

    destroy() {
        clearTimeout(this.quantityTimer);
        document.removeEventListener('cart:refresh', this.onCartRefresh);
        document.removeEventListener('click', this.checkoutGuard, true);
        document.removeEventListener('submit', this.checkoutGuard, true);
    }
})

customElements.define('cart-qty', class extends Core {
    propTypes = {
        value: Number,
        key: String,
        input: Boolean,
        'prevent-default': Boolean,
        line: Number,
        step: Number
    }

    render() {
        this.$(this.prop('input') ? {
            input: this._handleEvent,
            change: this._handleEvent
        } : { click: this._handleEvent });
    }

    _handleEvent(e) {
        if (this.hasAttribute('disabled')) return;
        if (this.prop('input') && e.type === 'input' &&
            (e.target.value === '' || !Number.isInteger(Number(e.target.value)) || Number(e.target.value) < 0)) return;
        this.prop('prevent-default') && e.preventDefault();
        this.pub(Events.CART_CHANGE, {
            key: this.prop('key'),
            line: this.prop('line'),
            quantity: this._getValue(e),
            optimistic: true
        })
    }

    _getValue(e) {
        if (this.prop('step')) {
            const row = this.closest('[data-item-key]');
            const input = row?.querySelector('[name="updates[]"]');
            const label = row?.querySelector('[data-cart-quantity]');
            const current = Number(input?.value ?? label?.textContent ?? 0);
            const max = Number(row?.dataset.maxQuantity);
            return Math.max(0, Math.min(current + this.prop('step'), row?.hasAttribute('data-max-quantity') ? max : Infinity));
        }
        return this.evt === 'change'
            ? this._getValidatedValue(e)
            : this.prop('value'); 
    }

    _getValidatedValue(e) {
        if (e.target.value === '') e.target.value = e.target.dataset.lastValid || e.target.defaultValue || '0';
        const value = Number(e.target.value);
        if (!Number.isInteger(value) || value < 0) {
            e.target.value = Math.max(0, Math.floor(value) || 0);
        }
        const maxValue = Number(e.target.getAttribute('max'));
        if (e.target.hasAttribute('max') && e.target.value > maxValue) {
            e.target.value = maxValue;
        }
        e.target.dataset.lastValid = e.target.value;
        return e.target.value;
    }

    get evt() {
        return this.prop('input') 
            ? 'change' 
            : 'click';
    }
});

customElements.define('cart-live-region', class extends Core {
    propTypes = {
        'target-section': String,
        'target-element': String
    }

    render() {
        this.targetSection = this.prop('target-section') || this.sectionId;
        this.targetSelector = this.prop('target-element') || `#${this.id}`;

        customElements.whenDefined('cart-provider').then(() => {
            this.pub(Events.CART_REGISTER, this.targetSection)
        })

        this.sub(Events.CART_UPDATE, this._updateContent, { global: true })
    }
    
    _updateContent({ sections }) {
        const targetSection = sections && sections[this.targetSection];
        if(!targetSection) {
            console.error(`target section ${targetSection} do not exists`);
            return;
        }

        const doc = Utils.parseHTML(targetSection);
        const target = doc.querySelector(this.targetSelector);
        if (target) Utils.$replaceContent(this, target);
    }
})
